// frontend/app/components/lessonPlans/lessonPlanRules.js
//
// Pure completion-rule engine for Lesson Plans. No React, no network — this is
// the piece worth unit-testing. The one dependency it does take is on
// lessonPlanTheme's section/phase vocabulary (SECTION_META, getStepSection),
// because the rail and the workspace header both need the same section
// grouping and neither may keep its own copy of it (see groupStepsBySection).
//
// Session state shape (immutable; never mutated in place):
//   {
//     [blockId]: {
//       value: any,          // answer payload — string, id, id[], ordered id[],
//                            // { [promptKey]: string, _confusion?: string } for
//                            // a reflection, { text, confidence } for a
//                            // prediction that captured confidence
//       isCorrect: boolean,  // graded checks only; null for every Loop type
//       launched: boolean,   // asset blocks: has the teacher opened it?
//       checked: boolean,    // submitted for feedback
//       aiScore: number,     // 0..1, written back by the AI evaluator
//       aiFeedback: object,  // { feedback, strengths, misconceptions, source }
//       answeredAt: number,  // epoch ms
//     }
//   }
//
// Rules are ADVISORY. They drive the ✓ on the phase rail and the progress
// figure; they never block navigation. A teacher running a class must always
// be able to move on, whatever the room has or hasn't answered yet.

import {
  getSectionMeta,
  getStepSection,
  PHASE_META,
  SECTION_KEYS,
} from "./lessonPlanTheme";

/** Block types that record an answer. Kept in sync with
 *  backend/services/lesson_phases.py CHECK_BLOCK_TYPES. */
export const CHECK_BLOCK_TYPES = [
  "multiple_choice",
  "multi_select",
  "short_answer",
  "ordering",
  "prediction",
  "explanation",
  "exploration_probe",
  "reflection",
  "next_challenge",
];

/** Block types that launch a Saras asset. */
export const ASSET_BLOCK_TYPES = ["saras_pwo", "saras_video", "saras_topic_quiz"];

/**
 * Blocks that answer nothing themselves but hold things a student answers.
 * The topic quiz is one block holding N bank questions; each question is
 * stored — here and on the server — under its own composite block id, because
 * twenty answers filed under one id would weigh what a single check weighs.
 *
 * Mirrors backend/services/lesson_phases.py CONTAINER_BLOCK_TYPES.
 */
export const CONTAINER_BLOCK_TYPES = ["saras_topic_quiz"];

export const isContainerBlock = (block) =>
  CONTAINER_BLOCK_TYPES.includes(block?.type);

/** Separates a container's authored id from the item answered inside it. */
export const COMPOSITE_BLOCK_SEPARATOR = ":";

/** `("quiz-1", 40213)` -> `"quiz-1:40213"`. */
export const compositeBlockId = (blockId, itemId) =>
  `${blockId}${COMPOSITE_BLOCK_SEPARATOR}${itemId}`;

/**
 * `"quiz-1:40213"` -> `{ blockId: "quiz-1", itemId: "40213" }`; anything else
 * -> `{ blockId: <input>, itemId: null }`.
 *
 * Mirrors `split_composite_block_id` in backend/services/mastery_evidence.py,
 * including both of its rules: split on the LAST separator, and only when the
 * tail is all digits. The two must agree — the client decides what id an
 * answer is saved under and the server decides what that id is evidence for.
 */
export const parseCompositeBlockId = (blockId) => {
  const text = String(blockId ?? "");
  const cut = text.lastIndexOf(COMPOSITE_BLOCK_SEPARATOR);
  const parent = text.slice(0, cut);
  const tail = text.slice(cut + 1);
  if (cut <= 0 || !/^[0-9]+$/.test(tail)) {
    return { blockId: text, itemId: null };
  }
  return { blockId: parent, itemId: tail };
};

/**
 * The five block types the Saras Learning Loop adds. Four of them record an
 * answer; `mastery_map` records nothing — it renders the student's own bands
 * back to them, so it is a display block, not a check.
 */
export const LOOP_BLOCK_TYPES = [
  "explanation",
  "reflection",
  "exploration_probe",
  "mastery_map",
  "next_challenge",
];

/** Blocks that only display. They can never be "answered", so they must never
 *  count toward completion — see ruleIsSatisfied. */
export const DISPLAY_BLOCK_TYPES = ["mastery_map"];

export const isCheckBlock = (block) =>
  CHECK_BLOCK_TYPES.includes(block?.type);

export const isAssetBlock = (block) =>
  ASSET_BLOCK_TYPES.includes(block?.type);

export const isLoopBlock = (block) => LOOP_BLOCK_TYPES.includes(block?.type);

export const isDisplayBlock = (block) =>
  DISPLAY_BLOCK_TYPES.includes(block?.type);

/** How strongly a lesson expects a teacher to use an asset. Mirrors
 *  backend/services/lesson_phases.py ASSET_TIERS. */
export const ASSET_TIER_CORE = "core";
export const ASSET_TIER_EXTENDED = "extended";

/**
 * Unknown or absent is `core`: an asset an author placed in a lesson is
 * expected to be used unless they said otherwise.
 *
 * Normalizes (trim + lowercase) before comparing. `lesson_seeder.py` guards
 * the file-authoring path, but the wizard, a direct API `PATCH`, and any
 * future import are not covered by that gate, so this must not depend on it
 * having run. Reading a stray `"Extended"` as `core` would be the worst
 * available failure here — it silently promotes an optional asset to
 * mandatory — so this reads defensively even though the write side is
 * strict.
 */
export const assetTierOf = (block) =>
  String(block?.asset_tier || "").trim().toLowerCase() === ASSET_TIER_EXTENDED
    ? ASSET_TIER_EXTENDED
    : ASSET_TIER_CORE;

/**
 * Groups a step's blocks for display. Non-asset blocks keep their authored
 * order; every asset block of the step is pulled out of it into at most two
 * groups, "core" then "extended", placed where the step's first asset sat.
 * Adjacency does not matter: a core asset authored after prose and an extended
 * one still land in the same core / extended groups.
 *
 * Each group is headed (the heading is what says which tier it is).
 *
 * Returns a flat list of entries in render order:
 *   { kind: "block", block }
 *   { kind: "assetGroup", tier, blocks, heading: true }
 */
export const groupBlocksByAssetTier = (blocks) => {
  const list = Array.isArray(blocks) ? blocks : [];
  const core = list.filter((b) => isAssetBlock(b) && assetTierOf(b) === ASSET_TIER_CORE);
  const extended = list.filter(
    (b) => isAssetBlock(b) && assetTierOf(b) === ASSET_TIER_EXTENDED
  );

  const groups = [];
  if (core.length > 0) {
    groups.push({ kind: "assetGroup", tier: ASSET_TIER_CORE, blocks: core, heading: true });
  }
  if (extended.length > 0) {
    groups.push({
      kind: "assetGroup",
      tier: ASSET_TIER_EXTENDED,
      blocks: extended,
      heading: true,
    });
  }

  const entries = [];
  let placed = false;
  list.forEach((block) => {
    if (!isAssetBlock(block)) {
      entries.push({ kind: "block", block });
    } else if (!placed) {
      entries.push(...groups);
      placed = true;
    }
  });

  return entries;
};

const entryFor = (sessionState, blockId) =>
  (sessionState && sessionState[blockId]) || null;

const isFilledText = (value) =>
  typeof value === "string" && value.trim().length > 0;

/**
 * A reflection is complete when every authored prompt carries text. The
 * "still confusing" field (`_confusion`) is a volunteer signal for the teacher,
 * never a requirement — a student with nothing left confusing is finished.
 */
export const reflectionIsComplete = (block, value) => {
  const prompts = Array.isArray(block?.prompts) ? block.prompts : [];
  const keys = prompts.map((prompt) => prompt?.key).filter(Boolean);
  if (keys.length === 0) return false;
  if (!value || typeof value !== "object") return false;
  return keys.every((key) => isFilledText(value[key]));
};

/**
 * Does this entry hold an answer? `block` is optional — pass it when the block's
 * own shape decides the question, as it does for a reflection's several prompts
 * and for a prediction that captured confidence alongside its text.
 */
const hasValue = (value, block) => {
  if (value === null || value === undefined) return false;
  if (block?.type === "reflection") return reflectionIsComplete(block, value);
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") {
    // { text, confidence } from a prediction, or a partially filled reflection
    // read without its block: any authored text counts.
    return Object.values(value).some(isFilledText);
  }
  return true;
};

const sameOrder = (a, b) =>
  Array.isArray(a) &&
  Array.isArray(b) &&
  a.length === b.length &&
  a.every((item, index) => item === b[index]);

const sameSet = (a, b) => {
  if (!Array.isArray(a) || !Array.isArray(b)) return false;
  if (a.length !== b.length) return false;
  const left = [...a].sort();
  const right = [...b].sort();
  return left.every((item, index) => item === right[index]);
};

/**
 * Grade an answer against the block's authored key.
 * Returns null for blocks that have no correct answer (predictions, free text),
 * so "ungraded" stays distinguishable from "wrong".
 */
export const gradeAnswer = (block, value) => {
  if (!block) return null;

  switch (block.type) {
    case "multiple_choice":
      if (!block.correct_option_id) return null;
      return value === block.correct_option_id;

    case "multi_select":
      if (!Array.isArray(block.correct_option_ids)) return null;
      return sameSet(value, block.correct_option_ids);

    case "ordering":
      if (!Array.isArray(block.correct_order)) return null;
      return sameOrder(value, block.correct_order);

    default:
      // short_answer, prediction and all five Loop types are not auto-graded.
      // The Loop's written work is scored by the AI evaluator on the server,
      // which writes aiScore back onto the entry — never by this function.
      return null;
  }
};

/**
 * Evaluate one completion rule against session state. `block` is optional; the
 * step-level callers pass the rule's own block so shape-dependent rules (a
 * reflection's several prompts) can be judged properly.
 */
export const ruleIsSatisfied = (rule, sessionState, block) => {
  if (!rule || typeof rule !== "object") return true;

  // A display block renders; it cannot be answered. A rule pointing at one
  // would gate the step shut forever, so it is treated as already satisfied.
  if (isDisplayBlock(block)) return true;

  const entry = entryFor(sessionState, rule.block_id);

  switch (rule.type) {
    case "answered":
      return Boolean(entry) && hasValue(entry.value, block);

    case "correct":
      return Boolean(entry) && entry.isCorrect === true;

    case "min_length": {
      if (!entry) return false;
      const text = typeof entry.value === "string" ? entry.value.trim() : "";
      return text.length >= (Number(rule.value) || 1);
    }

    case "launched":
      return Boolean(entry) && entry.launched === true;

    default:
      // An unknown rule must not silently gate the lesson shut.
      return true;
  }
};

const blockFor = (step, blockId) =>
  (Array.isArray(step?.blocks) ? step.blocks : []).find(
    (block) => block?.id === blockId
  ) || null;

/** A step is complete when every one of its rules is satisfied. */
export const stepIsComplete = (step, sessionState) => {
  const rules = Array.isArray(step?.completion_rules) ? step.completion_rules : [];
  if (rules.length === 0) return false;
  return rules.every((rule) =>
    ruleIsSatisfied(rule, sessionState, blockFor(step, rule.block_id))
  );
};

/** Fraction 0..1 of a step's rules currently satisfied. */
export const stepProgress = (step, sessionState) => {
  const rules = Array.isArray(step?.completion_rules) ? step.completion_rules : [];
  if (rules.length === 0) return 0;
  const done = rules.filter((rule) =>
    ruleIsSatisfied(rule, sessionState, blockFor(step, rule.block_id))
  ).length;
  return done / rules.length;
};

/**
 * Groups a lesson's steps into the four teacher-facing sections, preserving
 * step order within each group. A section with no steps in this lesson is
 * skipped entirely — a four-step lesson produces four single-step groups,
 * not four headings with gaps between them.
 *
 * A step whose section cannot be derived (getStepSection found nothing) is
 * never dropped: it lands in a trailing group keyed `null`, with the
 * fallback meta, regardless of where it fell in the authored order.
 *
 * The returned sections are always in `SECTION_KEYS` order (unknown last),
 * never in first-occurrence order. The four sections are a fixed conceptual
 * frame the SME asked for — always these four, always in this order — so a
 * rail showing "Assessment and Ensure" above "Explore and Explain" is wrong
 * regardless of how the lesson happens to be authored. This mirrors
 * `section_order()` in backend/services/lesson_phases.py, which exists for
 * the same reason; the frontend having its own divergent ordering was the
 * asymmetry worth removing. For a well-formed, phase-ordered lesson this
 * changes nothing — it only matters for a malformed lesson, where a stable
 * frame beats a traversal-order one.
 *
 * The rail and the workspace header both import this, so there is exactly
 * one place that turns seven phases into four sections — two copies of this
 * grouping would be the "third parallel keyed map" the original plan's
 * anti-goals forbid.
 */
export const groupStepsBySection = (steps) => {
  const list = Array.isArray(steps) ? steps : [];
  const groups = new Map();

  list.forEach((step, index) => {
    const key = getStepSection(step) || null;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push({ step, index });
  });

  // Steps with no derivable section are never interleaved with the known
  // ones — they trail the lesson, in the order they were authored.
  const unknown = groups.get(null);
  groups.delete(null);

  const sectionOrder = (key) => {
    const position = SECTION_KEYS.indexOf(key);
    return position === -1 ? SECTION_KEYS.length : position;
  };

  const sections = Array.from(groups.entries())
    .sort(([a], [b]) => sectionOrder(a) - sectionOrder(b))
    .map(([key, groupSteps]) => ({
      key,
      meta: getSectionMeta(key),
      steps: groupSteps,
    }));

  if (unknown) {
    sections.push({ key: null, meta: getSectionMeta(null), steps: unknown });
  }

  return sections;
};

const PHASE_LABELS_LOWER = Object.values(PHASE_META).map((meta) =>
  meta.label.toLowerCase()
);

/**
 * The title a teacher or student sees for a step. Phases are a hidden internal
 * key, so a title that is empty or merely a phase name ("Wonder", "explore")
 * never reaches the screen: a lone step in its section shows the section's
 * label, and a step among several shows "Step N". Any authored title is kept.
 *
 * `indexInSection` is zero-based; `t` is optional and translates the fallbacks.
 */
export const stepDisplayTitle = (step, indexInSection, sectionStepCount, t) => {
  const translate = t || ((value) => value);
  const authored = String(step?.title || "").trim();
  if (authored && !PHASE_LABELS_LOWER.includes(authored.toLowerCase())) {
    return authored;
  }
  const sectionKey = getStepSection(step);
  if (sectionKey && sectionStepCount === 1) {
    return translate(getSectionMeta(sectionKey).label);
  }
  const n = (Number(indexInSection) || 0) + 1;
  return translate("Step {n}").replace("{n}", String(n));
};

/** Lesson-level roll-up for the header progress figure. */
export const lessonProgress = (steps, sessionState) => {
  const list = Array.isArray(steps) ? steps : [];
  if (list.length === 0) {
    return { completedSteps: 0, totalSteps: 0, pct: 0 };
  }
  const completedSteps = list.filter((step) =>
    stepIsComplete(step, sessionState)
  ).length;
  return {
    completedSteps,
    totalSteps: list.length,
    pct: Math.round((100 * completedSteps) / list.length),
  };
};

/**
 * Immutable writer. Returns a NEW session state; the input is never mutated.
 * `patch` is merged over the existing entry so recording a launch does not
 * erase an answer, and vice versa.
 */
export const setBlockState = (sessionState, blockId, patch) => {
  if (!blockId) return sessionState || {};
  const current = (sessionState && sessionState[blockId]) || {};
  return {
    ...(sessionState || {}),
    [blockId]: { ...current, ...patch },
  };
};

/** Record an answer, grading it against the block's key in the same pass. */
export const recordAnswer = (sessionState, block, value) =>
  setBlockState(sessionState, block?.id, {
    value,
    isCorrect: gradeAnswer(block, value),
    answeredAt: Date.now(),
  });

/** Record that an asset block was opened. */
export const recordLaunch = (sessionState, blockId) =>
  setBlockState(sessionState, blockId, { launched: true });

/**
 * Record one answer to one question inside a container block.
 *
 * Written under the composite id rather than merged into the container's own
 * entry, so each question survives as its own answer with its own grade — the
 * shape `buildResponsePayload` needs to send twenty response rows instead of
 * one, and the shape the player reads back to resume a half-finished quiz.
 *
 * `isCorrect` is passed in rather than derived: grading a bank question needs
 * the question, and this module deliberately knows nothing about the bank.
 */
export const recordQuizAnswer = (
  sessionState,
  blockId,
  questionId,
  { value, isCorrect = null } = {}
) => {
  if (!blockId || questionId === null || questionId === undefined) {
    return sessionState || {};
  }
  return setBlockState(sessionState, compositeBlockId(blockId, questionId), {
    value,
    isCorrect,
    answeredAt: Date.now(),
  });
};

/**
 * Every answered question inside one container block, oldest id first.
 * Session state is flat, so the container's items are found by their prefix.
 */
export const quizAnswersFor = (sessionState, blockId) => {
  if (!blockId || !sessionState) return [];

  return Object.keys(sessionState)
    .map((key) => ({ key, parsed: parseCompositeBlockId(key) }))
    .filter(({ parsed }) => parsed.itemId !== null && parsed.blockId === blockId)
    .map(({ key, parsed }) => ({
      compositeId: key,
      questionId: parsed.itemId,
      entry: sessionState[key] || {},
    }))
    .filter(({ entry }) => hasValue(entry.value))
    .sort((a, b) => Number(a.questionId) - Number(b.questionId));
};

/**
 * Mark a block as submitted for feedback.
 *
 * Multi-select is graded on every toggle so the answer is never lost, but the
 * running grade must not be shown — a half-built selection is not a wrong
 * answer. Feedback waits for this flag.
 */
export const recordChecked = (sessionState, blockId) =>
  setBlockState(sessionState, blockId, { checked: true });

/**
 * Write an AI evaluation back onto the entry that produced it. Merged, so the
 * answer the student is still looking at survives the round trip.
 */
export const recordAiResult = (sessionState, blockId, { score, feedback } = {}) =>
  setBlockState(sessionState, blockId, {
    aiScore: score === undefined ? null : score,
    aiFeedback: feedback || null,
  });

/**
 * Project session state into the POST body for
 * /api/student/{email}/lesson-plans/{id}/responses.
 * Only blocks that were actually answered are sent.
 *
 * A container block contributes one response *per answered item*, under the
 * composite id the item was recorded with. That is what makes a twelve-question
 * quiz twelve pieces of evidence; the server resolves each id back to this
 * block to learn what they are evidence for.
 */
export const buildResponsePayload = (steps, sessionState) => {
  const responses = [];
  const list = Array.isArray(steps) ? steps : [];

  list.forEach((step) => {
    const blocks = Array.isArray(step?.blocks) ? step.blocks : [];
    blocks.forEach((block) => {
      if (isContainerBlock(block)) {
        quizAnswersFor(sessionState, block.id).forEach(({ compositeId, entry }) => {
          responses.push({
            step_key: step.step_key,
            block_id: compositeId,
            concept_key: block.concept_key || null,
            is_correct: entry.isCorrect === undefined ? null : entry.isCorrect,
            value: entry.value,
          });
        });
        return;
      }

      if (!isCheckBlock(block)) return;
      const entry = entryFor(sessionState, block.id);
      // Deliberately block-blind: a half-finished reflection is still work the
      // student did, and dropping it here would lose it on the next save.
      if (!entry || !hasValue(entry.value)) return;

      responses.push({
        step_key: step.step_key,
        block_id: block.id,
        concept_key: block.concept_key || null,
        is_correct: entry.isCorrect === undefined ? null : entry.isCorrect,
        value: entry.value,
      });
    });
  });

  return responses;
};
